/**
 * Resolve the active branch slug from the URL, mirroring v1's logic at
 * `online-ordering/src/app/app.component.ts::getRestaurantName/isSubdomainBased`.
 *
 * Two routing modes:
 *
 * 1. **Path-based** (platform / staging / dev): the slug is the first
 *    segment of the URL path. Example: `https://order.tappya.com/<slug>/cart`.
 *    React Router is given a `basename` of `/<slug>` so the rest of the
 *    routes (`/`, `/cart`, `/checkout`, …) render unchanged.
 *
 * 2. **Hostname-based** (custom domains): the slug is derived from the
 *    hostname itself. Example: `https://burgerinboxeg.com/cart` →
 *    slug = `burgerinboxeg.com`. Subdomains like `subdomain.tappya.com`
 *    use the leftmost label.
 *
 * Falls back to `VITE_DEFAULT_BRANCH_SLUG` for local dev.
 */

const PATH_BASED_DOMAINS = new Set<string>([
  "menu.tappya.com",
  "menu-staging.tappya.com",
  "tappya.com",
  "order.tappya.com",
  "order-staging.tappya.com",
  "digitalmenu.tappya.com",
  "getfoodpoint.com",
  "order.getfoodpoint.com",
  "order-staging.getfoodpoint.com",
  "digitalmenu.getfoodpoint.com",
  "menu.getfoodpoint.com",
]);

function stripWww(host: string): string {
  return host.startsWith("www.") ? host.slice(4) : host;
}

function isStagingHost(host: string): boolean {
  return (
    host.startsWith("staging") ||
    host.includes("ew.gateway.dev") ||
    host.includes("vercel.app") ||
    host.includes("amplifyapp.com")
  );
}

function getHostname(): string {
  // ?mockHostname=... is a v1 affordance for local custom-domain testing.
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search);
    const mock = params.get("mockHostname");
    if (mock) return mock;
  } catch {
    /* ignore */
  }
  return window.location.hostname;
}

function isLocalhost(host: string): boolean {
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local")
  );
}

function firstSegment(): string | null {
  if (typeof window === "undefined") return null;
  const segments = window.location.pathname
    .split("/")
    .filter((s) => s.length > 0);
  return segments[0] ?? null;
}

export type SlugResolution = {
  /** Resolved slug, or null if nothing matched. */
  slug: string | null;
  /** Router basename to use — `/${slug}` for path mode, "" for hostname mode. */
  basename: string;
  /** Diagnostic only. */
  mode: "path" | "hostname" | "fallback" | "none";
};

export function resolveSlug(): SlugResolution {
  const fallback =
    (import.meta.env.VITE_DEFAULT_BRANCH_SLUG as string | undefined) ?? null;

  if (typeof window === "undefined") {
    return { slug: fallback, basename: "", mode: fallback ? "fallback" : "none" };
  }

  const host = stripWww(getHostname());

  // Localhost dev: prefer path segment if present, else fall back to env var.
  if (isLocalhost(host)) {
    const seg = firstSegment();
    if (seg) {
      return { slug: seg, basename: `/${seg}`, mode: "path" };
    }
    return {
      slug: fallback,
      basename: "",
      mode: fallback ? "fallback" : "none",
    };
  }

  const isPlatform = PATH_BASED_DOMAINS.has(host) || isStagingHost(host);

  if (isPlatform) {
    const seg = firstSegment();
    if (seg) {
      return { slug: seg, basename: `/${seg}`, mode: "path" };
    }
    return {
      slug: fallback,
      basename: "",
      mode: fallback ? "fallback" : "none",
    };
  }

  // Custom domain → hostname-based.
  const parts = host.split(".");
  if (parts.length === 2) {
    return { slug: host, basename: "", mode: "hostname" };
  }
  if (parts.length >= 3) {
    return { slug: parts[0], basename: "", mode: "hostname" };
  }

  return { slug: host || fallback, basename: "", mode: "fallback" };
}
